import { useCallback, useMemo, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { Settings } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import { useAtomValue } from 'jotai';
import { DataTable } from '@/components/shared/Table';
import { UnitsEnum, UnitSystemEnum, useDecksWithDefectsQuery } from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { ColumnListingDialog } from './ColumnListingDialog';
import { localiseMeasurement } from '@/utils/unitSystem';
import { unitSystem as unitSystemState } from '@/components/Analysis/state';
import { TableSkeleton } from './TableSkeleton';
import { NOT_APPLICABLE_LABEL } from '@/constants';

export const DecksTable = () => {
  const structureId = useGetStructureId();
  const [columnsDialog, setColumnsDialog] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<GridColDef[]>([]);
  const unitSystem = useAtomValue(unitSystemState);

  const { data, loading } = useDecksWithDefectsQuery({
    variables: { structureId },
    fetchPolicy: 'cache-first',
  });

  const { defectsHeaders, defectsArea } = useMemo(() => {
    return {
      defectsHeaders: data?.allDefects || [],
      defectsArea: data?.decksWithDefects || [],
    };
  }, [data]);

  const formatArea = useCallback(
    (value: number) => {
      // function is converting meters to current unit system
      const convertedValue =
        localiseMeasurement({
          unitSystem,
          value,
          sourceUnit: UnitsEnum.SquareMeter,
        }) ?? NOT_APPLICABLE_LABEL;
      return new RegExp(/^\d+(\.\d+)?/).exec(convertedValue)?.[0] ?? convertedValue;
    },
    [unitSystem]
  );

  const unitSymbol = useMemo(() => {
    return unitSystem === UnitSystemEnum.Imperial ? '(ft²)' : '(m²)';
  }, [unitSystem]);

  const columns = useMemo(() => {
    const tableColumns: GridColDef[] = [
      {
        field: 'name',
        headerName: 'Deck',
        width: 100,
        flex: 1,
        type: 'string',
      },
      {
        field: 'totalDefects',
        headerName: `Total Defects Area ${unitSymbol}`,
        width: 100,
        flex: 1,
        type: 'number',
      },
      ...(defectsHeaders.map(({ key, name }) => {
        const column: GridColDef = {
          field: key,
          headerName: `${name} ${unitSymbol}`,
          width: 100,
          type: 'number',
          flex: 1,
        };
        return column;
      }) || []),
    ];
    const allColumns = [...tableColumns];
    setVisibleColumns(allColumns.splice(0, 6));
    return tableColumns;
  }, [defectsHeaders, unitSymbol]);

  const rows = useMemo(() => {
    return (
      defectsArea.map(({ id, name, defectArea }) => {
        const visibleDefects = defectArea.filter((defect) => {
          return visibleColumns.some((column) => column.field === defect.key);
        });

        const totalDefects = visibleDefects.reduce(
          (accumulator, defect) => accumulator + defect.area,
          0
        );

        return {
          id,
          name,
          totalDefects: formatArea(totalDefects),
          ...visibleDefects.reduce((accumulator, defect) => {
            return { ...accumulator, [defect.key]: defect.area ? formatArea(defect.area) : 'N/A' };
          }, {}),
        };
      }) || []
    );
  }, [defectsArea, formatArea, visibleColumns]);

  if (loading) {
    return <TableSkeleton />;
  }

  return (
    <>
      <Box sx={{ textAlign: 'right', mb: 1 }}>
        <IconButton onClick={() => setColumnsDialog(true)}>
          <Settings />
        </IconButton>
      </Box>
      <DataTable
        columns={visibleColumns}
        rows={rows}
        pageSizeOptions={[10, 20, 50]}
        selected={false}
      />
      <ColumnListingDialog
        open={columnsDialog}
        onClose={() => setColumnsDialog(false)}
        columns={columns}
        setVisibleColumns={setVisibleColumns}
        visibleColumns={visibleColumns}
      />
    </>
  );
};
